'use client';

import { useState, useEffect } from 'react';

import CountrySelector from './CountrySelector';
import CategoryFilter from './CategoryFilter';
import NewsCard from './NewsCard';
import NewsCardAd from '@/components/adsense/NewsCardAd';
import PromoBanner from './PromoBanner';
import SearchBar, { type SearchFilters } from './SearchBar';
import RightSidebar from './RightSidebar';
import AppImage from '@/components/ui/AppImage';
import { trackCategoryEngagement } from '@/lib/analytics';
import { type NewsArticle as SupabaseArticle } from '@/lib/supabase/newsService';
import { fetchCountryNews, type FormattedNewsArticle } from '@/lib/newsApiService';
import { fetchMultipleRSSFeeds, DEFAULT_RSS_SOURCES } from '@/lib/rssFeedService';
import { fetchSocialFeeds, DEFAULT_SOCIAL_CONFIGS } from '@/lib/socialFeedService';
import { detectUserCountry } from '@/lib/geolocation';


import Link from 'next/link';
import { useRouter } from 'next/navigation';

interface NewsArticle {
  id: string;
  title: string;
  summary: string;
  image: string;
  imageAlt: string;
  source: {
    name: string;
    logo: string;
    logoAlt: string;
    url: string;
  };
  category: string;
  timestamp: string;
  readTime: string;
  isVideo?: boolean;
  country: string;
  platform?: string;
  engagement?: number;
  isHotTopic?: boolean;
  views?: number;
}

interface HomepageInteractiveProps {
  initialCountry?: string;
}

const translateText = async (text: string, targetLang: string): Promise<string> => {
  if (targetLang === 'en') return text;
  const langPrefixes: Record<string, string> = {
    hi: '[HI] ', ta: '[TA] ', te: '[TE] ', es: '[ES] ', zh: '[ZH] ', id: '[ID] ',
    pt: '[PT] ', ur: '[UR] ', yo: '[YO] ', ha: '[HA] ', bn: '[BN] ', ru: '[RU] ',
    ja: '[JA] ', tl: '[TL] ', vi: '[VI] ', tr: '[TR] ', de: '[DE] ', fr: '[FR] ',
    th: '[TH] ', it: '[IT] ', ms: '[MS] '
  };
  return (langPrefixes[targetLang] || '') + text;
};

const mockNews: NewsArticle[] = [
{
  id: 'news_facebook_001',
  title: 'Meta Announces Major Update to Facebook News Feed Algorithm',
  summary: 'Facebook parent company Meta revealed significant changes to how content appears in user feeds. The new algorithm prioritizes meaningful interactions and local news sources. This update represents a fundamental shift in how the platform curates content for its billions of users worldwide. The changes aim to promote authentic engagement while reducing the spread of misinformation. Industry experts suggest this could reshape social media dynamics significantly. Users can expect to see more content from friends and family, along with trusted local news outlets. The rollout will be gradual over the coming months, with continuous refinements based on user feedback and engagement metrics.',
  image: "https://images.unsplash.com/photo-1504137013456-40118f92cac3",
  imageAlt: 'Person using Facebook app on smartphone',
  source: { name: 'Facebook Newsroom', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_11cdfe271-1770928663867.png", logoAlt: 'Facebook logo', url: 'https://facebook.com' },
  category: 'Technology', timestamp: '25 mins ago', readTime: '1 min', country: 'WORLD', platform: 'facebook', engagement: 125000, views: 1250000
},
{
  id: 'news_instagram_001',
  title: 'Instagram Introduces Revolutionary AR Shopping Experience',
  summary: 'Instagram launched an augmented reality feature allowing users to virtually try on fashion items before purchasing. This groundbreaking technology combines artificial intelligence with advanced computer vision to create realistic virtual fitting rooms. Shoppers can now see how clothes, accessories, and makeup products look on them in real-time using their smartphone cameras. The feature integrates seamlessly with Instagram Shopping, making the purchase process smooth and intuitive. Fashion brands are already reporting increased conversion rates and reduced return rates. This innovation could transform the entire e-commerce landscape by bridging the gap between online and in-store shopping experiences.',
  image: "https://images.unsplash.com/photo-1723864059860-636dfc060b9d",
  imageAlt: 'Woman using Instagram AR filter',
  source: { name: 'Instagram Official', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1efb8dff8-1770928658928.png", logoAlt: 'Instagram logo', url: 'https://instagram.com' },
  category: 'Fashion', timestamp: '35 mins ago', readTime: '1 min', country: 'WORLD', platform: 'instagram', engagement: 89000, isHotTopic: true, views: 890000
},
{
  id: 'news_x_001',
  title: 'X Platform Launches Premium Video Content Hub for Creators',
  summary: 'X (formerly Twitter) unveiled a new monetization platform for video creators, competing directly with YouTube. The platform offers enhanced revenue sharing models, advanced analytics tools, and exclusive creator support programs. Content creators can now earn money through subscriptions, tips, and advertising revenue. The new video hub features improved streaming quality, better discovery algorithms, and comprehensive creator tools. Early adopters are already seeing significant earnings, with some top creators reporting six-figure monthly revenues. This move positions X as a serious competitor in the creator economy space.',
  image: "https://images.unsplash.com/photo-1694834931303-3208b7df2ecc",
  imageAlt: 'Content creator recording video',
  source: { name: 'X News', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_19711b00c-1770928658533.png", logoAlt: 'X platform logo', url: 'https://x.com' },
  category: 'Technology', timestamp: '40 mins ago', readTime: '1 min', country: 'WORLD', platform: 'x', engagement: 156000, views: 1560000
},
{
  id: 'news_fashion_001',
  title: 'Paris Fashion Week Unveils Sustainable Luxury Collections',
  summary: 'Leading fashion houses presented groundbreaking sustainable collections at Paris Fashion Week.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_104b0107d-1769199259656.png",
  imageAlt: 'Models walking runway at Paris Fashion Week',
  source: { name: 'Vogue', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_12fd8a7cf-1770928660466.png", logoAlt: 'Vogue magazine logo', url: 'https://vogue.com' },
  category: 'Fashion', timestamp: '50 mins ago', readTime: '1 min', country: 'WORLD', platform: 'fashion', engagement: 234000, isHotTopic: true, views: 2340000
},
{
  id: 'news_fashion_002',
  title: 'Gen Z Drives Vintage Fashion Market to $50 Billion Valuation',
  summary: 'The secondhand fashion market reached unprecedented growth driven by Gen Z consumers prioritizing sustainability.',
  image: "https://images.unsplash.com/photo-1725352118209-48b97b2c2ac6",
  imageAlt: 'Colorful vintage clothing displayed',
  source: { name: 'Elle', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1770a52dc-1770928658450.png", logoAlt: 'Elle magazine logo', url: 'https://elle.com' },
  category: 'Fashion', timestamp: '1 hour ago', readTime: '1 min', country: 'WORLD', engagement: 178000, views: 1780000
},
{
  id: 'news_x_002',
  title: 'Breaking: Global Leaders Use X for Real-Time Climate Summit Updates',
  summary: 'World leaders at the Climate Action Summit are providing unprecedented real-time updates via X platform.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1781b7ace-1767117542231.png",
  imageAlt: 'World leaders at climate summit',
  source: { name: 'X News', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_19711b00c-1770928658533.png", logoAlt: 'X platform logo', url: 'https://x.com' },
  category: 'Politics', timestamp: '1 hour ago', readTime: '1 min', country: 'WORLD', platform: 'x', engagement: 892000, isHotTopic: true, views: 8920000
},
{
  id: 'news_instagram_002',
  title: 'Instagram Reels Surpasses TikTok in Daily Active Users',
  summary: 'Instagram Reels achieved a major milestone, overtaking TikTok with 2.1 billion daily active users.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10750679b-1767080724646.png",
  imageAlt: 'Influencer creating Instagram Reels content',
  source: { name: 'Instagram Official', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1efb8dff8-1770928658928.png", logoAlt: 'Instagram logo', url: 'https://instagram.com' },
  category: 'Technology', timestamp: '2 hours ago', readTime: '1 min', country: 'WORLD', platform: 'instagram', engagement: 445000, views: 4450000
},
{
  id: 'news_facebook_002',
  title: 'Facebook Groups Become Primary News Source for 1.5 Billion Users',
  summary: 'Research reveals Facebook Groups have evolved into major news distribution channels.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_153c77e0e-1768207376316.png",
  imageAlt: 'Diverse group of people engaged in community discussion',
  source: { name: 'Facebook Newsroom', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_11cdfe271-1770928663867.png", logoAlt: 'Facebook logo', url: 'https://facebook.com' },
  category: 'Technology', timestamp: '2 hours ago', readTime: '1 min', country: 'WORLD', platform: 'facebook', engagement: 267000, views: 2670000
},
{
  id: 'news_fashion_003',
  title: 'AI-Designed Fashion Line Sells Out in 3 Hours at Milan Fashion Week',
  summary: 'An artificial intelligence-designed fashion collection made history by selling out completely within three hours of launch.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b2a5ef8b-1770893940106.png",
  imageAlt: 'Futuristic AI-designed fashion garments',
  source: { name: 'Harper\'s Bazaar', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1737edf5e-1764660485858.png", logoAlt: 'Harper\'s Bazaar logo', url: 'https://harpersbazaar.com' },
  category: 'Fashion', timestamp: '3 hours ago', readTime: '1 min', country: 'WORLD', platform: 'fashion', engagement: 312000, isHotTopic: true, views: 3120000
},
{
  id: 'news_bbc_001',
  title: 'BBC Launches Interactive Documentary Series on Climate Change',
  summary: 'The BBC unveiled an innovative documentary series allowing viewers to make decisions affecting the narrative outcome.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1752a2290-1770655316299.png",
  imageAlt: 'BBC studio with interactive climate change documentary',
  source: { name: 'BBC News', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c493f27c-1770928658688.png", logoAlt: 'BBC News logo', url: 'https://bbc.com/news' },
  category: 'Entertainment', timestamp: '3 hours ago', readTime: '1 min', country: 'WORLD', engagement: 198000, views: 1980000
},
{
  id: 'news_cnn_001',
  title: 'CNN Introduces AI-Powered Real-Time Fact-Checking During Live Broadcasts',
  summary: 'CNN implemented groundbreaking AI technology that fact-checks statements in real-time during live news broadcasts.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fdf661bb-1770655314983.png",
  imageAlt: 'CNN newsroom with advanced AI fact-checking technology',
  source: { name: 'CNN', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_18035f47c-1770928660141.png", logoAlt: 'CNN logo', url: 'https://cnn.com' },
  category: 'Technology', timestamp: '4 hours ago', readTime: '1 min', country: 'WORLD', engagement: 423000, views: 4230000
},
{
  id: 'news_x_003',
  title: 'Viral Thread Exposes Corporate Greenwashing Practices',
  summary: 'Environmental activist X thread detailing corporate greenwashing tactics reached 20 million views in 48 hours.',
  image: "https://images.unsplash.com/photo-1553522911-f8607a133a83",
  imageAlt: 'Environmental protest signs',
  source: { name: 'X Activism', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_19711b00c-1770928658533.png", logoAlt: 'X platform logo', url: 'https://x.com' },
  category: 'Business', timestamp: '5 hours ago', readTime: '1 min', country: 'WORLD', platform: 'x', engagement: 678000, isHotTopic: true, views: 6780000
},
{
  id: 'news_instagram_003',
  title: 'Fashion Influencer Launches Zero-Waste Clothing Line',
  summary: 'Top Instagram fashion influencer with 15 million followers launched completely zero-waste clothing brand.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10ca289a1-1768211137583.png",
  imageAlt: 'Eco-friendly fashion collection',
  source: { name: 'Instagram Fashion', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1efb8dff8-1770928658928.png", logoAlt: 'Instagram logo', url: 'https://instagram.com' },
  category: 'Fashion', timestamp: '6 hours ago', readTime: '1 min', country: 'WORLD', platform: 'instagram', engagement: 523000, views: 5230000
},
{
  id: 'news_facebook_003',
  title: 'Facebook Marketplace Disrupts Traditional Retail with $100B in Sales',
  summary: 'Facebook Marketplace reached $100 billion in annual transaction volume, challenging traditional e-commerce platforms.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_12079448e-1770655308980.png",
  imageAlt: 'Person browsing Facebook Marketplace',
  source: { name: 'Facebook Business', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_11cdfe271-1770928663867.png", logoAlt: 'Facebook logo', url: 'https://facebook.com' },
  category: 'Business', timestamp: '7 hours ago', readTime: '1 min', country: 'WORLD', platform: 'facebook', engagement: 345000, views: 3450000
},
{
  id: 'news_fashion_004',
  title: 'Digital Fashion NFTs Generate $2 Billion Market',
  summary: 'Virtual fashion items and NFTs created a $2 billion market as metaverse adoption accelerates.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1596a0e04-1765333053081.png",
  imageAlt: 'Digital fashion NFT displayed',
  source: { name: 'WWD', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_15d521456-1770928661324.png", logoAlt: 'WWD logo', url: 'https://wwd.com' },
  category: 'Fashion', timestamp: '8 hours ago', readTime: '1 min', country: 'WORLD', engagement: 289000, views: 2890000
},
{
  id: 'news_youtube_001',
  title: 'YouTube Shorts Monetization Reaches $10 Billion for Creators',
  summary: 'YouTube announced that Shorts creators have collectively earned over $10 billion through the platform\'s monetization program.',
  image: "https://images.unsplash.com/photo-1603713575261-354c201cd853",
  imageAlt: 'YouTube creator filming content',
  source: { name: 'YouTube Official', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_120269007-1770928659019.png", logoAlt: 'YouTube logo', url: 'https://youtube.com' },
  category: 'Technology', timestamp: '9 hours ago', readTime: '1 min', country: 'WORLD', platform: 'youtube', engagement: 567000, isVideo: true, views: 5670000
},
{
  id: 'news_linkedin_001',
  title: 'LinkedIn Introduces AI Career Coach for Professional Development',
  summary: 'LinkedIn launched an AI-powered career coaching tool that provides personalized guidance for career advancement.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e72d1ecf-1768637634806.png",
  imageAlt: 'Professional networking event',
  source: { name: 'LinkedIn News', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1cddb9543-1770928659483.png", logoAlt: 'LinkedIn logo', url: 'https://linkedin.com' },
  category: 'Business', timestamp: '10 hours ago', readTime: '1 min', country: 'WORLD', platform: 'linkedin', engagement: 234000, views: 2340000
},
{
  id: 'news_tiktok_001',
  title: 'TikTok Shop Surpasses $20 Billion in Annual Sales',
  summary: 'TikTok\'s e-commerce platform achieved a major milestone, surpassing $20 billion in annual sales globally.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c9256cff-1770655945109.png",
  imageAlt: 'TikTok shopping interface',
  source: { name: 'TikTok Business', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_13dc28f9f-1770928663826.png", logoAlt: 'TikTok logo', url: 'https://tiktok.com' },
  category: 'Business', timestamp: '11 hours ago', readTime: '1 min', country: 'WORLD', engagement: 789000, isHotTopic: true, views: 7890000
},
{
  id: 'news_snapchat_001',
  title: 'Snapchat AR Lenses Used by 300 Million Daily Users',
  summary: 'Snapchat reported that over 300 million users engage with augmented reality lenses daily, marking significant growth.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_15c6bd52e-1770009107366.png",
  imageAlt: 'Person using Snapchat AR filter',
  source: { name: 'Snapchat News', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1cfdb8fe3-1770928659560.png", logoAlt: 'Snapchat logo', url: 'https://snapchat.com' },
  category: 'Technology', timestamp: '12 hours ago', readTime: '1 min', country: 'WORLD', engagement: 456000, views: 4560000
},
{
  id: 'news_pinterest_001',
  title: 'Pinterest Launches AI-Powered Interior Design Tool',
  summary: 'Pinterest unveiled an AI tool that helps users visualize and plan interior design projects with virtual room staging.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_167be0da3-1767674642192.png",
  imageAlt: 'Modern interior design visualization',
  source: { name: 'Pinterest Blog', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_12c7fb787-1766493594524.png", logoAlt: 'Pinterest logo', url: 'https://pinterest.com' },
  category: 'Technology', timestamp: '13 hours ago', readTime: '1 min', country: 'WORLD', engagement: 312000, views: 3120000
},
{
  id: 'news_reddit_001',
  title: 'Reddit Communities Drive $5 Billion in Charitable Donations',
  summary: 'Reddit announced that community-driven fundraising campaigns have collectively raised over $5 billion for various causes.',
  image: "https://images.unsplash.com/photo-1720423698770-317b60058ffd",
  imageAlt: 'Community charity event',
  source: { name: 'Reddit News', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_10b8f3cb7-1770928660611.png", logoAlt: 'Reddit logo', url: 'https://reddit.com' },
  category: 'Social', timestamp: '14 hours ago', readTime: '1 min', country: 'WORLD', engagement: 678000, isHotTopic: true, views: 6780000
},
{
  id: 'news_twitch_001',
  title: 'Twitch Streamers Earn Record $3 Billion in 2026',
  summary: 'Twitch reported that content creators on the platform earned a record-breaking $3 billion in 2026 through subscriptions and donations.',
  image: "https://images.unsplash.com/photo-1733169340008-8ab4dd090e70",
  imageAlt: 'Gaming streamer setup',
  source: { name: 'Twitch News', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_102b58dfa-1770928659573.png", logoAlt: 'Twitch logo', url: 'https://twitch.tv' },
  category: 'Entertainment', timestamp: '15 hours ago', readTime: '1 min', country: 'WORLD', platform: 'twitch', engagement: 890000, isVideo: true, views: 8900000
},
{
  id: 'news_discord_001',
  title: 'Discord Expands to 500 Million Registered Users Worldwide',
  summary: 'Discord reached a major milestone with 500 million registered users, becoming one of the largest communication platforms.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10b7d80b6-1764776560079.png",
  imageAlt: 'Discord community chat interface',
  source: { name: 'Discord Blog', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1e793c523-1770928660915.png", logoAlt: 'Discord logo', url: 'https://discord.com' },
  category: 'Technology', timestamp: '16 hours ago', readTime: '1 min', country: 'WORLD', engagement: 445000, views: 4450000
},
{
  id: 'news_spotify_001',
  title: 'Spotify Podcasts Surpass 5 Million Active Shows',
  summary: 'Spotify announced that its podcast platform now hosts over 5 million active shows, marking exponential growth in audio content.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1baabf2db-1768415916308.png",
  imageAlt: 'Podcast recording studio',
  source: { name: 'Spotify News', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1e3fa9b09-1767166212643.png", logoAlt: 'Spotify logo', url: 'https://spotify.com' },
  category: 'Entertainment', timestamp: '17 hours ago', readTime: '1 min', country: 'WORLD', engagement: 567000, views: 5670000
},
{
  id: 'news_netflix_001',
  title: 'Netflix Interactive Content Reaches 100 Million Viewers',
  summary: 'Netflix reported that interactive storytelling content has been viewed by over 100 million subscribers worldwide.',
  image: "https://images.unsplash.com/photo-1539786723186-577e290c1bee",
  imageAlt: 'Person watching Netflix on TV',
  source: { name: 'Netflix Media', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1720cbd1c-1770928659120.png", logoAlt: 'Netflix logo', url: 'https://netflix.com' },
  category: 'Entertainment', timestamp: '18 hours ago', readTime: '1 min', country: 'WORLD', engagement: 723000, isVideo: true, views: 7230000
},
{
  id: 'news_whatsapp_001',
  title: 'WhatsApp Business Reaches 200 Million Active Users Globally',
  summary: 'WhatsApp Business platform announced it now serves over 200 million active business accounts worldwide, revolutionizing customer communication.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11b407bb8-1767210873764.png",
  imageAlt: 'Business owner using WhatsApp Business',
  source: { name: 'WhatsApp Blog', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_17d15b8fc-1766516151075.png", logoAlt: 'WhatsApp logo', url: 'https://whatsapp.com' },
  category: 'Business', timestamp: '19 hours ago', readTime: '1 min', country: 'WORLD', engagement: 445000, views: 4450000
},
{
  id: 'news_medium_001',
  title: 'Medium Writers Collectively Earn $500 Million Through Partner Program',
  summary: 'Medium announced that independent writers on its platform have earned over $500 million through the Medium Partner Program since its inception.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bf96a152-1768069013906.png",
  imageAlt: 'Writer working on laptop',
  source: { name: 'Medium Blog', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_12e203466-1770928658946.png", logoAlt: 'Medium logo', url: 'https://medium.com' },
  category: 'Technology', timestamp: '20 hours ago', readTime: '1 min', country: 'WORLD', engagement: 312000, views: 3120000
},
{
  id: 'news_clubhouse_001',
  title: 'Clubhouse Pivots to Professional Networking with New Features',
  summary: 'Audio social platform Clubhouse unveiled major updates focusing on professional networking and industry-specific rooms.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e72d1ecf-1768637634806.png",
  imageAlt: 'Professional networking event',
  source: { name: 'Clubhouse News', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_17d2b59b2-1770928661003.png", logoAlt: 'Clubhouse logo', url: 'https://clubhouse.com' },
  category: 'Business', timestamp: '21 hours ago', readTime: '1 min', country: 'WORLD', engagement: 189000, views: 1890000
},
{
  id: 'news_vimeo_001',
  title: 'Vimeo Launches AI-Powered Video Editing Suite for Creators',
  summary: 'Vimeo introduced an advanced AI-powered video editing platform that automates complex editing tasks for professional creators.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e2b3e806-1765958046272.png",
  imageAlt: 'Video editing workspace',
  source: { name: 'Vimeo Blog', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1f5901876-1770928659169.png", logoAlt: 'Vimeo logo', url: 'https://vimeo.com' },
  category: 'Technology', timestamp: '22 hours ago', readTime: '1 min', country: 'WORLD', platform: 'vimeo', engagement: 267000, isVideo: true, views: 2670000
},
{
  id: 'news_substack_001',
  title: 'Substack Newsletter Writers Surpass $300 Million in Annual Earnings',
  summary: 'Substack revealed that independent newsletter writers on the platform are collectively earning over $300 million annually through paid subscriptions.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bf8e3b2a-1769237419684.png",
  imageAlt: 'Newsletter writer at desk',
  source: { name: 'Substack', logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1a4398d57-1770928659970.png", logoAlt: 'Substack logo', url: 'https://substack.com' },
  category: 'Business', timestamp: '23 hours ago', readTime: '1 min', country: 'WORLD', engagement: 234000, isHotTopic: true, views: 2340000
}];


export default function HomepageInteractive({ initialCountry = 'WORLD' }: HomepageInteractiveProps) {
  const [selectedCountry, setSelectedCountry] = useState(initialCountry || 'WORLD');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(10);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [autoDetectedCountry, setAutoDetectedCountry] = useState<string | null>(null);
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    keyword: '',
    dateFrom: '',
    dateTo: '',
    category: 'all',
    source: 'all'
  });
  const router = useRouter();

  // Auto-detect user country on mount
  useEffect(() => {
    const detectCountry = async () => {
      const detectedCountry = await detectUserCountry();
      setAutoDetectedCountry(detectedCountry);

      // Only set as selected country if no initial country provided
      if (!initialCountry || initialCountry === 'WORLD') {
        setSelectedCountry(detectedCountry);
      }
    };

    detectCountry();
  }, []);

  useEffect(() => {
    const loadNews = async () => {
      setLoading(true);
      setError(null);
      try {
        let articles: NewsArticle[] = [];

        // Fetch news from multiple sources: API, RSS feeds, and social media
        try {
          // 1. Fetch from News API with AI processing
          const apiArticles = await fetchCountryNews(
            selectedCountry,
            selectedCategory === 'all' ? undefined : selectedCategory,
            20
          );

          if (apiArticles && apiArticles.length > 0) {
            articles = apiArticles.map((article) => ({
              ...article,
              isVideo: false
            }));
          }

          // 2. Fetch from RSS feeds
          const rssArticles = await fetchMultipleRSSFeeds(DEFAULT_RSS_SOURCES);
          if (rssArticles && rssArticles.length > 0) {
            articles.push(...rssArticles.map((article) => ({
              ...article,
              isVideo: false
            })));
          }

          // 3. Fetch from social media feeds
          const socialArticles = await fetchSocialFeeds(DEFAULT_SOCIAL_CONFIGS, selectedCountry);
          if (socialArticles && socialArticles.length > 0) {
            articles.push(...socialArticles);
          }
        } catch (apiError) {
          console.error('Content fetch error:', apiError);
        }

        // Fallback to mock news if all sources return empty
        if (articles.length === 0) {
          console.warn(`No news found for ${selectedCountry}, using fallback content`);
          articles = mockNews.filter((article) =>
          selectedCountry === 'WORLD' || article.country === selectedCountry
          );
        }

        // Apply category filter
        if (selectedCategory !== 'all') {
          articles = articles.filter(
            (article) => article.category.toLowerCase() === selectedCategory.toLowerCase()
          );
        }

        // Ensure we always have some content to display
        if (articles.length === 0) {
          articles = mockNews.slice(0, 5);
        }

        setNews(articles);
      } catch (err) {
        console.error('Error loading news:', err);
        setError('Failed to load news. Showing fallback content.');
        setNews(mockNews.slice(0, 10));
      } finally {
        setLoading(false);
      }
    };

    loadNews();
  }, [selectedCountry, selectedCategory]);

  const filteredNews = selectedCategory === 'all' ?
  news :
  selectedCategory === 'world' ?
  news.filter((article) => article.country === 'WORLD') :
  news.filter((article) => article.category.toLowerCase() === selectedCategory.toLowerCase());

  // Filter by country - show only news from selected country, or all news if WORLD is selected
  const countryFilteredNews = selectedCountry === 'WORLD' ?
  filteredNews :
  filteredNews.filter((article) => article.country === selectedCountry);

  // Apply search filters
  const searchFilteredNews = countryFilteredNews.filter((article) => {
    // Keyword filter
    if (searchFilters.keyword) {
      const keyword = searchFilters.keyword.toLowerCase();
      const matchesKeyword =
      article.title.toLowerCase().includes(keyword) ||
      article.summary.toLowerCase().includes(keyword) ||
      article.source.name.toLowerCase().includes(keyword);
      if (!matchesKeyword) return false;
    }

    // Date range filter
    if (searchFilters.dateFrom || searchFilters.dateTo) {
      // Parse article timestamp (e.g., "25 mins ago", "2 hours ago", "1 day ago")
      const now = new Date();
      let articleDate = now;

      const timeMatch = article.timestamp.match(/(\d+)\s+(min|hour|day|week)s?\s+ago/);
      if (timeMatch) {
        const value = parseInt(timeMatch[1]);
        const unit = timeMatch[2];

        articleDate = new Date(now);
        if (unit === 'min') articleDate.setMinutes(now.getMinutes() - value);else
        if (unit === 'hour') articleDate.setHours(now.getHours() - value);else
        if (unit === 'day') articleDate.setDate(now.getDate() - value);else
        if (unit === 'week') articleDate.setDate(now.getDate() - value * 7);
      }

      if (searchFilters.dateFrom) {
        const fromDate = new Date(searchFilters.dateFrom);
        if (articleDate < fromDate) return false;
      }

      if (searchFilters.dateTo) {
        const toDate = new Date(searchFilters.dateTo);
        toDate.setHours(23, 59, 59, 999); // End of day
        if (articleDate > toDate) return false;
      }
    }

    // Category filter from search
    if (searchFilters.category !== 'all') {
      if (article.category.toLowerCase() !== searchFilters.category.toLowerCase()) {
        return false;
      }
    }

    // Source filter
    if (searchFilters.source !== 'all') {
      if (article.source.name !== searchFilters.source) {
        return false;
      }
    }

    return true;
  });

  const visibleNews = searchFilteredNews.slice(0, visibleCount);
  const hasMore = visibleCount < searchFilteredNews.length;

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + 10);
  };

  const handleNext = () => {
    if (currentCardIndex < filteredNews.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(currentCardIndex - 1);
    }
  };

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrevious();
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentCardIndex, filteredNews.length]);

  const handleCountryChange = (country: string) => {
    setSelectedCountry(country);
    setCurrentCardIndex(0);
    if (country !== 'WORLD') {
      router.push(`/${country.toLowerCase()}`);
    }
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentCardIndex(0);
    trackCategoryEngagement(category, selectedCountry);
  };

  const handleSearch = (filters: SearchFilters) => {
    setSearchFilters(filters);
    setVisibleCount(10); // Reset visible count on new search
    setCurrentCardIndex(0);
  };

  // Get unique categories and sources for filter dropdowns
  const availableCategories = Array.from(new Set(news.map((article) => article.category)));
  const availableSources = Array.from(new Set(news.map((article) => article.source.name))).sort();

  // Convert news articles to trending format for sidebar
  const trendingItems = filteredNews.slice(0, 8).map((article) => ({
    id: article.id,
    title: article.title,
    image: article.image,
    imageAlt: article.imageAlt,
    views: article.views || 0,
    category: article.category,
    timestamp: article.timestamp
  }));

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/homepage" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <AppImage src="/assets/images/intagg_logo-01-1770559550357.png" alt="Intaggs Logo" width={40} height={40} className="object-contain" />
            <div className="flex flex-col">
              <h1 className="font-heading font-bold text-2xl text-primary">Intaggs</h1>
              <p className="text-xs text-gray-600">news in 100 words</p>
            </div>
          </Link>
          <CountrySelector
            currentCountry={selectedCountry}
            onCountryChange={handleCountryChange}
            selectedLanguage={selectedLanguage}
            onLanguageChange={setSelectedLanguage} />
          
        </div>
        
        {/* Category Filter with increased vertical spacing */}
        <div className="mb-10 mt-10 flex items-center justify-center">
          <CategoryFilter selectedCategory={selectedCategory} onCategoryChange={handleCategoryChange} />
        </div>
      </header>

      {/* Promotional Banner - After Categories */}
      <PromoBanner />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Search Bar */}
        <SearchBar
          onSearch={handleSearch}
          availableCategories={availableCategories}
          availableSources={availableSources} />
        

        {error &&
        <div className="mb-4 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg">
            {error}
          </div>
        }
        
        {loading ?
        <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading news...</p>
            </div>
          </div> :

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content Area - News Cards */}
            <div className="lg:col-span-2">
              <div className="max-w-full">
                <div className="grid grid-cols-1 gap-6">
                  {visibleNews.map((article, index) =>
                <div key={article.id}>
                      <NewsCard {...article} articleId={article.id} views={article.views} />
                      {/* Insert AdSense ad every 5 cards */}
                      {(index + 1) % 5 === 0 && index !== visibleNews.length - 1 &&
                  <div className="my-6">
                          <NewsCardAd country={selectedCountry} />
                        </div>
                  }
                    </div>
                )}
                </div>
                
                {/* Load More Button */}
                {hasMore &&
              <div className="mt-8 flex justify-center">
                    <button
                  onClick={handleLoadMore}
                  className="px-8 py-3 bg-white text-black font-semibold text-base rounded border-2 border-black hover:bg-black hover:text-white transition-all duration-300">
                      Load More
                    </button>
                  </div>
              }
              </div>
            </div>
            
            {/* Right Sidebar - Trending Now */}
            <div className="lg:col-span-1">
              <RightSidebar trendingItems={trendingItems} />
            </div>
          </div>
        }
      </main>
    </div>);


}